// =============================================================================
// FILE: packages/renderer/src/components/branding/AboutDialog.tsx
// PURPOSE: Settings → About modal. Lists product identity, creator credits,
//          version, build, and database version (read from the diagnostics
//          IPC so we never invent numbers).
// =============================================================================

import { useQuery } from '@tanstack/react-query';
import { useEffect, type JSX } from 'react';
import { LogoMark } from './LogoMark.js';
import { call } from '../../lib/api.js';
import { IPC } from '@bim/shared/constants/ipc.constants.js';
import { getBrand } from '@bim/shared/config/brand.config.js';

interface DbInfo { sqliteVersion: string; pragmas: { userVersion: number } }

interface AboutDialogProps { open: boolean; onClose: () => void; }

export function AboutDialog({ open, onClose }: AboutDialogProps): JSX.Element | null {
  const brand = getBrand();
  const info = useQuery<DbInfo>({
    queryKey: ['diagnostics', 'db-info'],
    queryFn:  () => call<undefined, DbInfo>(IPC.DIAGNOSTICS_DB_INFO, undefined),
    enabled:  open,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const version  = '1.0.0';
  const build    = '2026-06-28';
  const dbVer    = info.data ? `${info.data.pragmas.userVersion} · SQLite ${info.data.sqliteVersion}` : '—';

  return (
    <div
      data-testid="about-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm"
      style={{ background: 'rgba(10,22,46,0.80)' }}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md mx-4 rounded-2xl p-8 shadow-2xl"
        style={{
          background: 'rgb(var(--panel))',
          border: '1px solid rgb(var(--border)/0.6)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col items-center text-center">
          <div className="h-24 w-24 rounded-2xl flex items-center justify-center mb-2"
            style={{ background: 'rgba(20,184,166,0.10)', border: '1px solid rgba(20,184,166,0.28)' }}>
            <LogoMark size={68} className="text-teal-400" />
          </div>
          <div className="mt-3 text-2xl font-extrabold tracking-[0.22em]" style={{ color: 'rgb(var(--fg))' }}>
            BIM&nbsp;OPS
          </div>
          <div className="mt-1 text-[11px] uppercase tracking-[0.26em]" style={{ color: 'rgb(var(--muted)/0.55)' }}>
            {brand.tagline.replace(/\.$/, '')}
          </div>
        </div>

        <div className="mt-7 pt-5 space-y-3 text-[13px]"
          style={{ borderTop: '1px solid rgb(var(--border)/0.4)' }}>
          <Row label="Created by"  value={brand.creator.name} small />
          <Row label="Role"        value={brand.creator.role} small />
          <Row label="Location"    value={brand.creator.location} small />
          <div className="my-3" style={{ borderTop: '1px solid rgb(var(--border)/0.25)' }} />
          <Row label="Version"          value={version} mono />
          <Row label="Build"            value={build} mono />
          <Row label="Database version" value={dbVer} mono />
        </div>

        <div className="mt-7 flex items-center justify-between text-[10px]"
          style={{ color: 'rgb(var(--muted)/0.5)' }}>
          <span>{brand.copyright}</span>
          <button
            type="button"
            data-testid="about-dialog-close"
            onClick={onClose}
            className="px-3 py-1.5 uppercase tracking-[0.18em] rounded-xl transition"
            style={{
              border: '1px solid rgb(var(--border))',
              color: 'rgb(var(--fg)/0.75)',
              background: 'transparent',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, mono, small }: { label: string; value: string; mono?: boolean; small?: boolean }): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: 'rgb(var(--muted)/0.55)' }}>
        {label}
      </span>
      <span
        className={mono ? 'font-mono text-[12px]' : small ? 'text-[11px] font-light tracking-wide' : ''}
        style={{ color: 'rgb(var(--fg)/0.75)' }}
      >
        {value}
      </span>
    </div>
  );
}
